import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { LECTURE_TYPES } from '@classCreation/data/lectureTypes';
import AcademyModuleInput from '../AcademyModuleInput';
import AcademyTopicResourceType from 'propTypes/academyTopicResourceType';
import Input from 'components/Input';
import LectureBucketSelect from '../LectureBucketSelect';
import LectureTypeSelect from '@classCreation/components/LectureTypeSelect';
import styles from './AcademyTopicInput.module.scss';

const parseLectureBucket = (lectureBucket) => {
  return lectureBucket
    ? {
        label: lectureBucket?.title,
        value: lectureBucket?.id,
      }
    : null;
};

const parseLectureType = (lectureType) => {
  return lectureType
    ? { label: LECTURE_TYPES.optional, value: LECTURE_TYPES.optional }
    : { label: LECTURE_TYPES.regular, value: LECTURE_TYPES.regular };
};

export default function AcademyTopicInput({
  value,
  onChange,
  disabled = false,
}) {
  const [academyModule, setAcademyModule] = useState(value?.academy_module);
  const [lectureBucket, setLectureBucket] = useState(
    parseLectureBucket(value?.lecture_bucket)
  );
  const [title, setTitle] = useState(value?.title || '');
  const [junctionNumber, setJunctionNumber] = useState(value?.junction_number);
  const [lectureType, setLectureType] = useState(
    parseLectureType(value?.lecture_bucket)
  );

  useEffect(() => {
    setLectureType(parseLectureType(value?.lecture_bucket));
    setLectureBucket(parseLectureBucket(value?.lecture_bucket));
    setAcademyModule(value?.academy_module);
    setTitle(value?.title || '');
    setJunctionNumber(value?.junction_number);
  }, [value]);

  const handleLectureTypeChange = useCallback(
    (option) => {
      setLectureType(option);
      if (option.value === LECTURE_TYPES.regular) {
        setLectureBucket(null);
        onChange({
          ...value,
          lecture_bucket: null,
        });
      }
    },
    [onChange, value]
  );

  const handleLectureNameChange = useCallback(
    (event) => {
      setTitle(event.target.value);
      onChange({ ...value, title: event.target.value });
    },
    [onChange, value]
  );

  const handleJunctionNumberChange = useCallback(
    (event) => {
      setJunctionNumber(event.target.value);
      onChange({ ...value, junction_number: event.target.value });
    },
    [onChange, value]
  );

  const handleLectureBucketChange = useCallback(
    (option) => {
      setLectureBucket(option);
      onChange({
        ...value,
        lecture_bucket: { id: option.value, title: option.label },
      });
    },
    [onChange, value]
  );

  const handleAcademyModuleChange = useCallback(
    (newAcademyModule) => {
      setAcademyModule(newAcademyModule);
      onChange({
        ...value,
        academy_module: newAcademyModule,
      });
    },
    [onChange, value]
  );

  return (
    <>
      <Input
        className={classNames(styles.input, 'm-b-xs')}
        onChange={handleLectureNameChange}
        value={title}
        disabled={disabled}
        placeholder="Enter Topic Name"
      />
      <div className="row m-b-xs">
        <LectureTypeSelect
          onChange={handleLectureTypeChange}
          value={lectureType}
          isDisabled={disabled}
          className={classNames(styles.input, 'm-r-xs')}
        />
        {lectureType?.label === LECTURE_TYPES.optional && (
          <LectureBucketSelect
            onChange={handleLectureBucketChange}
            value={lectureBucket}
            isDisabled={disabled}
            className={styles.input}
          />
        )}
      </div>
      <AcademyModuleInput
        onChange={handleAcademyModuleChange}
        value={academyModule}
        className={styles.input}
        disabled={disabled}
      />
      <Input
        className={classNames(styles.input, 'm-t-xs')}
        onChange={handleJunctionNumberChange}
        type="number"
        value={junctionNumber}
        disabled={disabled}
        placeholder="Enter Junction Number"
      />
      <div className="warning m-t-xxs h6 italic">
        Please leave junction number blank if current class is not a junction.
      </div>
    </>
  );
}

AcademyTopicInput.propTypes = {
  value: AcademyTopicResourceType,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
